import { parseISO, setDate, setMonth, setYear, startOfDay } from 'date-fns'
import { format, toZonedTime } from 'date-fns-tz'
import { DateRange } from 'react-day-picker'

import { isSameDate } from '@/utils/date-converter'

import { Preset } from './constants'

export const getSelectedPreset = (
  presets: Preset[],
  date: DateRange | undefined,
  dateRangeByPreset: (preset: Preset) => DateRange,
): Preset | undefined => {
  return date
    ? presets.find((preset) => {
        const range = dateRangeByPreset(preset)

        return (
          isSameDate(range.from!, date.from) && isSameDate(range.to!, date.to)
        )
      })
    : undefined
}

export const formatDateRange = (
  currentDate: DateRange | undefined,
  today: Date,
  forceYear = false,
) => {
  if (!currentDate || !currentDate.from || !currentDate.to) {
    return ''
  }

  const longFormat = 'LLL dd, yyyy'
  const shortFormat = forceYear ? longFormat : 'LLL dd'

  if (isSameDate(currentDate.from, currentDate.to)) {
    return format(currentDate.from, shortFormat)
  }

  return `${format(
    currentDate.from,
    currentDate.from.getFullYear() === currentDate.to.getFullYear() &&
      currentDate.from.getFullYear() === today.getFullYear()
      ? shortFormat
      : longFormat,
  )} - ${format(
    currentDate.to,
    currentDate.from.getFullYear() === currentDate.to.getFullYear() &&
      currentDate.from.getFullYear() === today.getFullYear()
      ? shortFormat
      : longFormat,
  )}`
}

export const areDateRangesIntersecting = (
  range1: DateRange,
  range2: DateRange,
): boolean => {
  if (!range1.from || !range1.to || !range2.from || !range2.to) {
    return false
  }

  return (
    startOfDay(range1.from) <= startOfDay(range2.to) &&
    startOfDay(range2.from) <= startOfDay(range1.to)
  )
}

export const clampDate = (date: Date, range: DateRange): Date => {
  if (range.from && date < range.from) return range.from
  if (range.to && date > range.to) return range.to

  return date
}

export const parseStringDate = (date: string, today: Date): Date => {
  if (!date || !today) {
    return new Date(0)
  }

  const parsedDate = parseISO(date)

  let newDate = today

  newDate = setDate(newDate, parsedDate.getDate())
  newDate = setMonth(newDate, parsedDate.getMonth())
  newDate = setYear(newDate, parsedDate.getFullYear())

  return newDate
}

export const getDateInTimezone = (
  timezone: string | undefined,
  date = new Date(),
): Date => {
  if (!timezone) {
    return date
  }

  const zonedDate = toZonedTime(date, timezone)

  return zonedDate
}

export const getUtcOffsetLabel = (timezone: string): string => {
  const zonedDate = toZonedTime(new Date(), timezone)
  const offset = format(zonedDate, 'XXX', { timeZone: timezone })

  if (offset === 'Z') {
    return 'UTC+00:00'
  }

  return `UTC${offset}`
}
